import { sleep } from "@/lib/utils"

import type { Api, InfrastructureCosts, IntegrationState, IntegrationsOverview } from "../types"

// Офлайн-зеркало раздела «Интеграции» в виде, в котором его увидит
// администратор боевого контура на 21.09.2026: Робокасса выложена, но в .env
// стоит PAYMENT_PROVIDER=cloudpayments — консоль обязана показать это явно.
const hoursAgo = (h: number) => new Date(Date.now() - h * 3_600_000).toISOString()

const card = (over: Partial<IntegrationState> & Pick<IntegrationState, "key" | "group" | "title">): IntegrationState => ({
	provider: over.key,
	status: "connected",
	active: true,
	configured: true,
	statusNote: "",
	missing: [],
	details: [],
	impact: "",
	checkable: true,
	monthlyFee: null,
	...over,
})

let metrika: string | null = null
let costs: InfrastructureCosts = {
	servers: 22150,
	serversNote: "Со слов заказчика на 26.09.2026",
	fees: {},
	updatedAt: hoursAgo(2),
	updatedBy: null,
	totalMonthly: 22150,
}

const overview = (): IntegrationsOverview => ({
	environment: "production",
	payments: {
		configuredProvider: "cloudpayments",
		activeProvider: "cloudpayments",
		testMode: false,
		demoTopup: false,
		warnings: [
			"Робокасса — основной провайдер с 17.09.2026, но на этом контуре оплату принимает CloudPayments (PAYMENT_PROVIDER=cloudpayments). Для Робокассы не хватает: ROBOKASSA_TEST_PASSWORD1, ROBOKASSA_TEST_PASSWORD2.",
		],
	},
	items: [
		card({
			key: "robokassa",
			group: "payments",
			title: "Робокасса",
			status: "no_keys",
			active: false,
			configured: false,
			statusNote: "Не хватает ключей тестового режима. Оплату сейчас принимает CloudPayments.",
			missing: ["ROBOKASSA_TEST_PASSWORD1", "ROBOKASSA_TEST_PASSWORD2"],
			details: [{ label: "Режим", value: "тестовый (ROBOKASSA_IS_TEST=true)" }],
			impact: "Оплата заказов на витрине и пополнение баланса — основной провайдер с 17.09.2026",
			checkable: false,
		}),
		card({
			key: "cloudpayments",
			group: "payments",
			title: "CloudPayments",
			statusNote: "Принимает оплату: PAYMENT_PROVIDER=cloudpayments.",
			details: [{ label: "Валюта", value: "RUB" }],
			impact: "Запасной платёжный провайдер: виджет оплаты картой, возвраты",
			lastOkAt: hoursAgo(3),
		}),
		card({
			key: "sbp",
			group: "payments",
			title: "СБП",
			provider: "не подключено",
			status: "not_connected",
			active: false,
			configured: false,
			statusNote: "Отдельного подключения к СБП нет.",
			checkable: false,
		}),
		card({
			key: "sdek",
			group: "delivery",
			title: "СДЭК",
			statusNote: "Включён и считает доставку.",
			settingsPath: "/delivery",
			settingsLabel: "Доставка → Перевозчики",
			lastOkAt: hoursAgo(1),
			lastErrorAt: hoursAgo(20),
			lastError: "СДЭК oauth/token responded 502",
		}),
		card({
			key: "pecom",
			group: "delivery",
			title: "ПЭК",
			status: "no_keys",
			active: false,
			configured: false,
			statusNote: "Нет ключей — включение в разделе «Доставка» его не оживит.",
			missing: ["PECOM_LOGIN", "PECOM_API_KEY"],
			settingsPath: "/delivery",
			settingsLabel: "Доставка → Перевозчики",
			checkable: false,
		}),
		card({
			key: "sms",
			group: "messaging",
			title: "SMS (СМС-Центр)",
			provider: "smsc",
			statusNote: "Коды и уведомления уходят через СМС-Центр.",
			settingsPath: "/integrations#sms",
			settingsLabel: "Журнал SMS ниже",
		}),
		card({
			key: "email",
			group: "messaging",
			title: "Почта",
			provider: "yandex",
			statusNote: "Письма уходят через Яндекс 360.",
			settingsPath: "/email",
			settingsLabel: "Почта",
		}),
		card({ key: "dadata", group: "data", title: "DaData", statusNote: "Проверка ИНН и подсказки адресов." }),
		card({
			key: "openrouter",
			group: "ai",
			title: "OpenRouter (ИИ)",
			statusNote: "ИИ-блоки отчётов и ассистент продавца работают.",
			settingsPath: "/reports",
			settingsLabel: "Отчёты",
		}),
		card({
			key: "metrika",
			group: "analytics",
			title: "Яндекс.Метрика",
			provider: metrika ? "yandex-metrika" : "не подключено",
			status: metrika ? "connected" : "no_keys",
			active: Boolean(metrika),
			configured: Boolean(metrika),
			statusNote: metrika ? `Счётчик ${metrika} подключается на витрине.` : "Номер счётчика не задан.",
			details: [{ label: "Номер счётчика", value: metrika ?? "не задан" }],
			missing: ["METRIKA_OAUTH_TOKEN"],
			checkable: true,
		}),
		card({
			key: "moysklad",
			group: "accounting",
			title: "МойСклад",
			provider: "moysklad",
			status: "inactive",
			active: false,
			statusNote: "Готово к подключению: продавец вставляет свой токен МойСклад в кабинете. Пока никто не подключил.",
			details: [{ label: "Подключено магазинов", value: "0" }],
		}),
		card({
			key: "onec",
			group: "accounting",
			title: "1С (обмен с сайтом)",
			provider: "onec",
			status: "inactive",
			active: false,
			statusNote: "Готово к подключению: продавец включает обмен в кабинете и вписывает адрес и API-ключ в 1С.",
			details: [{ label: "Адрес обмена для 1С", value: "https://api.osmix.ru/1c_exchange" }],
		}),
		card({ key: "s3", group: "infrastructure", title: "Хранилище файлов (S3 Garage)", provider: "garage" }),
		card({
			key: "edo",
			group: "documents",
			title: "ЭДО",
			provider: "не подключено",
			status: "not_connected",
			active: false,
			configured: false,
			statusNote: "Интеграции с оператором электронного документооборота в площадке нет.",
			checkable: false,
		}),
	].map((item) => ({ ...item, monthlyFee: costs.fees[item.key] ?? null })),
})

export const integrations: Api["integrations"] = {
	async overview() {
		await sleep(150)
		return overview()
	},
	async check(key) {
		await sleep(300)
		return { key, ok: true, message: "Авторизация прошла (демо)", checkedAt: new Date().toISOString(), durationMs: 300 }
	},
	async costs() {
		await sleep(100)
		return costs
	},
	async updateCosts(patch) {
		await sleep(150)
		const fees = { ...costs.fees }
		for (const [k, v] of Object.entries(patch.fees ?? {})) {
			if (v == null) delete fees[k]
			else fees[k] = v
		}
		const servers = patch.servers === undefined ? costs.servers : patch.servers
		costs = {
			...costs,
			servers,
			serversNote: patch.serversNote ?? costs.serversNote,
			fees,
			updatedAt: new Date().toISOString(),
			updatedBy: "Платформа",
			totalMonthly: (servers ?? 0) + Object.values(fees).reduce<number>((a, b) => a + (b ?? 0), 0),
		}
		return costs
	},
	async setMetrikaCounter(counterId) {
		await sleep(100)
		metrika = counterId?.trim() || null
		return { counterId: metrika }
	},
	// API-ключи магазинов (П5): в офлайн-режиме ключей нет.
	async apiKeys() {
		await sleep(100)
		return []
	},
	async revokeApiKey() {
		await sleep(100)
		throw new Error("Ключ не найден")
	},
}
